import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ConversionUploadStats {
  uploaded: number;
  failed: number;
  queued: number;
  reversal_pending: number;
  last_uploaded_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
}

export interface ConversionSetup {
  lead_action_resource: string;
  sale_action_resource: string;
  auto_tagging_enabled: boolean;
  ecl_enabled: boolean;
  conversion_tracking_id: string | null;
  account_timezone: string | null;
  account_currency: string | null;
  setup_at: string;
  checked_at: string;
}

const STATS_KEY = ['conversion-upload-stats', 7] as const;
const INTEGRATION_KEY = ['integration-status', 'google_ads'] as const;

/** Conversion-upload stats + the "Repair setup" mutation for the Google Ads card. */
export function useGoogleAdsConversions(enabled = true) {
  const queryClient = useQueryClient();

  const statsQuery = useQuery<ConversionUploadStats | null>({
    queryKey: STATS_KEY,
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_conversion_upload_stats', { p_days: 7 });
      if (error) throw error;
      const row = (data ?? [])[0] as Partial<ConversionUploadStats> | undefined;
      if (!row) return null;
      return {
        uploaded: Number(row.uploaded ?? 0),
        failed: Number(row.failed ?? 0),
        queued: Number(row.queued ?? 0),
        reversal_pending: Number(row.reversal_pending ?? 0),
        last_uploaded_at: row.last_uploaded_at ?? null,
        last_error: row.last_error ?? null,
        last_error_at: row.last_error_at ?? null,
      };
    },
  });

  const setupMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('google-ads-setup', { body: {} });
      if (error) {
        // supabase-js wraps non-2xx in a FunctionsHttpError; surface the body's error code.
        const ctx = (error as { context?: Response }).context;
        let detail = error.message ?? 'Setup failed';
        try {
          const body = ctx ? await ctx.clone().json() : null;
          if (body?.error) detail = body.detail ? `${body.error}: ${body.detail}` : String(body.error);
        } catch {
          /* keep the generic message */
        }
        throw new Error(detail);
      }
      return data as { ok: boolean; setup: ConversionSetup };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATION_KEY });
      queryClient.invalidateQueries({ queryKey: STATS_KEY });
    },
  });

  return {
    stats: statsQuery.data ?? null,
    isLoadingStats: statsQuery.isLoading,
    refetchStats: statsQuery.refetch,
    runSetup: setupMutation.mutateAsync,
    isRunningSetup: setupMutation.isPending,
  };
}
